import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { api } from '../lib/api-client';
import type { DrawStroke, DrawStrokePoint, DrawToolType } from '@spine-workspace/shared';

interface DrawingState {
  drawings: Record<number, DrawStroke[]>;
  redoStacks: Record<number, DrawStroke[]>;
  framesLoaded: Record<number, boolean>;
  brushColor: string;
  brushSize: number;
  isEraser: boolean;
  filled: boolean;
  activeDrawTool: DrawToolType;
  visible: boolean;
  selectedStrokeId: string | null;

  setBrushColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setActiveDrawTool: (tool: DrawToolType) => void;
  toggleEraser: () => void;
  toggleFilled: () => void;
  toggleVisible: () => void;
  selectStroke: (id: string | null) => void;
  commitStroke: (sceneId: string, frame: number, points: DrawStrokePoint[], opts?: { type?: DrawToolType; text?: string; filled?: boolean }) => Promise<void>;
  undoLastStroke: (sceneId: string, frame: number) => Promise<void>;
  redoLastStroke: (sceneId: string, frame: number) => Promise<void>;
  removeStroke: (sceneId: string, frame: number, strokeId: string) => Promise<void>;
  moveStroke: (sceneId: string, frame: number, strokeId: string, dx: number, dy: number) => Promise<void>;
  clearFrame: (sceneId: string, frame: number) => Promise<void>;
  loadFrame: (sceneId: string, frame: number) => Promise<void>;
  reset: () => void;
}

export const useDrawingStore = create<DrawingState>((set, get) => ({
  drawings: {},
  redoStacks: {},
  framesLoaded: {},
  brushColor: '#ef4444',
  brushSize: 4,
  isEraser: false,
  filled: false,
  activeDrawTool: 'pen',
  visible: true,
  selectedStrokeId: null,

  setBrushColor: (color) => set({ brushColor: color, isEraser: false }),
  setBrushSize: (size) => set({ brushSize: size }),
  setActiveDrawTool: (tool) => set({ activeDrawTool: tool, isEraser: false, selectedStrokeId: null }),
  toggleEraser: () => set((s) => ({ isEraser: !s.isEraser })),
  toggleFilled: () => set((s) => ({ filled: !s.filled })),
  toggleVisible: () => set((s) => ({ visible: !s.visible })),
  selectStroke: (id) => set({ selectedStrokeId: id }),

  commitStroke: async (sceneId, frame, points, opts) => {
    if (points.length < 1) return;
    const { brushColor, brushSize, isEraser, activeDrawTool, filled } = get();
    const type = opts?.type ?? activeDrawTool;
    // Pen/line/arrow/rect/ellipse need >=2 points; text needs 1
    if (type !== 'text' && points.length < 2) return;

    const stroke: DrawStroke = {
      id: nanoid(16),
      type,
      color: brushColor,
      size: brushSize,
      isEraser: type !== 'text' ? isEraser : false,
      points,
      text: opts?.text,
      filled: opts?.filled ?? (type !== 'pen' && type !== 'text' ? filled : false),
    };
    // New stroke clears the redo stack for this frame
    set((s) => ({
      drawings: { ...s.drawings, [frame]: [...(s.drawings[frame] ?? []), stroke] },
      redoStacks: { ...s.redoStacks, [frame]: [] },
    }));
    try {
      await api.post(`/api/scenes/${sceneId}/drawings/${frame}`, stroke);
    } catch {
      set((s) => ({
        drawings: { ...s.drawings, [frame]: (s.drawings[frame] ?? []).filter((st) => st.id !== stroke.id) },
      }));
    }
  },

  undoLastStroke: async (sceneId, frame) => {
    const frameStrokes = get().drawings[frame] ?? [];
    if (frameStrokes.length === 0) return;
    const last = frameStrokes[frameStrokes.length - 1];
    set((s) => ({
      drawings: { ...s.drawings, [frame]: (s.drawings[frame] ?? []).slice(0, -1) },
      redoStacks: { ...s.redoStacks, [frame]: [...(s.redoStacks[frame] ?? []), last] },
    }));
    try {
      await api.delete(`/api/scenes/${sceneId}/drawings/${frame}/${last.id}`);
    } catch {
      // Rollback: restore the stroke and remove from redo stack
      set((s) => ({
        drawings: { ...s.drawings, [frame]: [...(s.drawings[frame] ?? []), last] },
        redoStacks: { ...s.redoStacks, [frame]: (s.redoStacks[frame] ?? []).slice(0, -1) },
      }));
    }
  },

  redoLastStroke: async (sceneId, frame) => {
    const stack = get().redoStacks[frame] ?? [];
    if (stack.length === 0) return;
    const stroke = stack[stack.length - 1];
    set((s) => ({
      drawings: { ...s.drawings, [frame]: [...(s.drawings[frame] ?? []), stroke] },
      redoStacks: { ...s.redoStacks, [frame]: (s.redoStacks[frame] ?? []).slice(0, -1) },
    }));
    try {
      await api.post(`/api/scenes/${sceneId}/drawings/${frame}`, stroke);
    } catch {
      // Rollback: remove from drawings and restore to redo stack
      set((s) => ({
        drawings: { ...s.drawings, [frame]: (s.drawings[frame] ?? []).filter((st) => st.id !== stroke.id) },
        redoStacks: { ...s.redoStacks, [frame]: [...(s.redoStacks[frame] ?? []), stroke] },
      }));
    }
  },

  removeStroke: async (sceneId, frame, strokeId) => {
    const frameStrokes = get().drawings[frame] ?? [];
    const stroke = frameStrokes.find((s) => s.id === strokeId);
    if (!stroke) return;
    set((s) => ({
      drawings: { ...s.drawings, [frame]: (s.drawings[frame] ?? []).filter((st) => st.id !== strokeId) },
      redoStacks: { ...s.redoStacks, [frame]: [...(s.redoStacks[frame] ?? []), stroke] },
      selectedStrokeId: null,
    }));
    try {
      await api.delete(`/api/scenes/${sceneId}/drawings/${frame}/${strokeId}`);
    } catch {
      set((s) => ({
        drawings: { ...s.drawings, [frame]: [...(s.drawings[frame] ?? []), stroke] },
        redoStacks: { ...s.redoStacks, [frame]: (s.redoStacks[frame] ?? []).slice(0, -1) },
      }));
    }
  },

  moveStroke: async (sceneId, frame, strokeId, dx, dy) => {
    const frameStrokes = get().drawings[frame] ?? [];
    const stroke = frameStrokes.find((s) => s.id === strokeId);
    if (!stroke) return;
    const newPoints = stroke.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    const newStroke = { ...stroke, points: newPoints };
    set((s) => ({
      drawings: {
        ...s.drawings,
        [frame]: (s.drawings[frame] ?? []).map((st) => (st.id === strokeId ? newStroke : st)),
      },
    }));
    try {
      await api.patch(`/api/scenes/${sceneId}/drawings/${frame}/${strokeId}`, { points: newPoints });
    } catch {
      set((s) => ({
        drawings: {
          ...s.drawings,
          [frame]: (s.drawings[frame] ?? []).map((st) => (st.id === strokeId ? stroke : st)),
        },
      }));
    }
  },

  clearFrame: async (sceneId, frame) => {
    const prev = get().drawings[frame] ?? [];
    set((s) => ({ drawings: { ...s.drawings, [frame]: [] }, redoStacks: { ...s.redoStacks, [frame]: [] } }));
    try {
      await api.delete(`/api/scenes/${sceneId}/drawings/${frame}`);
    } catch {
      set((s) => ({ drawings: { ...s.drawings, [frame]: prev } }));
    }
  },

  loadFrame: async (sceneId, frame) => {
    if (get().framesLoaded[frame]) return;
    try {
      const res = await api.get<{ strokes: DrawStroke[] }>(`/api/scenes/${sceneId}/drawings/${frame}`);
      set((s) => ({
        drawings: { ...s.drawings, [frame]: res.strokes },
        framesLoaded: { ...s.framesLoaded, [frame]: true },
      }));
    } catch {
      // non-critical
    }
  },

  reset: () => set({ drawings: {}, redoStacks: {}, framesLoaded: {}, visible: true, selectedStrokeId: null }),
}));
